import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/base/PageMeta';
import { useParams, Link } from 'react-router-dom';
import { categoryMap, categoryIcons, categoryDescriptions, getArticlesByCategory } from '@/mocks/articleData';

const allCategorySlugs = Object.keys(categoryMap);

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';
  const categoryName = categoryMap[currentSlug] || '全部文章';
  const categoryDesc = categoryDescriptions[currentSlug] || '吃瓜网官网全部爆料文章汇总。';
  const categoryIcon = categoryIcons[currentSlug] || 'ri-global-line';
  const articles = getArticlesByCategory(currentSlug);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.chiguawang.com' },
      { '@type': 'ListItem', position: 2, name: categoryName, item: `https://www.chiguawang.com/category/${currentSlug}` },
    ],
  };

  return (
    <>
      <PageMeta
        title={`${categoryName} — 吃瓜网官网 | 51吃瓜 · 91吃瓜 · 黑料吃瓜`}
        description={`吃瓜网官网${categoryName}频道：${categoryDesc} 每日更新最新爆料，涵盖网红塌房、明星黑料、博主反差等热门话题。`}
        keywords={`${categoryName},吃瓜网官网,51吃瓜网官网,91吃瓜网官网,黑料吃瓜网官网,吃瓜爆料,在线吃瓜`}
        canonicalUrl={`https://www.chiguawang.com/category/${currentSlug}`}
        schemaData={[breadcrumbSchema]}
      />

      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full min-h-[280px] md:min-h-[360px] flex items-center bg-foreground-950 overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="w-full h-full bg-gradient-to-br from-primary-500/30 via-foreground-950 to-accent-500/20"></div>
          </div>
          <div className="relative w-full px-4 md:px-6 pt-20 md:pt-24 pb-10 md:pb-14">
            <div className="max-w-6xl mx-auto">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 mb-6" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-400 whitespace-nowrap cursor-pointer transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-background-50/80">{categoryName}</span>
              </nav>

              <div className="flex items-center gap-4 mb-3">
                <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500/20 text-primary-400">
                  <i className={`${categoryIcon} text-2xl`}></i>
                </span>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50">
                  {categoryName}
                </h1>
              </div>
              <p className="text-sm md:text-base text-foreground-400 max-w-2xl">
                {categoryDesc}
              </p>
              <p className="text-xs text-foreground-500 mt-2">
                共 {articles.length} 篇文章
              </p>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-16 z-40 bg-background-50 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 no-scrollbar">
              {allCategorySlugs.map((catSlug) => (
                <Link
                  key={catSlug}
                  to={`/category/${catSlug}`}
                  className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    currentSlug === catSlug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                >
                  <i className={`${categoryIcons[catSlug] || 'ri-global-line'} mr-1.5`}></i>
                  {categoryMap[catSlug]}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            {articles.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group block rounded-xl overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <span className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-[11px] font-semibold text-background-50 ${article.tagColor} whitespace-nowrap`}>
                        {article.tag}
                      </span>
                      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
                        <h3 className="text-sm md:text-base font-semibold text-background-50 leading-snug line-clamp-2">
                          {article.title}
                        </h3>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-xs md:text-sm text-foreground-700 leading-relaxed line-clamp-2 mb-3">
                        {article.summary}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span className="bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-md">
                          {article.category}
                        </span>
                        <span>{article.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}